// Leaflet (the map library) is fetched from unpkg the first time a map is
// shown, pinned to 1.9.4 with SRI hashes so a tampered copy is refused (this
// window runs with Node integration, so that matters). The Insights page's
// flier targeting map shares it.
//
// Tiles are OpenStreetMap's own. CARTO's, used before, started coming back
// watermarked "API key required" in 2026. OSM's are free for light use like
// this as long as the attribution stays visible.
import { useEffect, useState } from 'react';

const LEAFLET_JS = {
  src: 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js',
  integrity: 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=',
};
const LEAFLET_CSS = {
  href: 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css',
  integrity: 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=',
};

export const TILES = {
  url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
};

// Hockley and the villages around it
export const HOME_VIEW = { center: [51.6, 0.64], zoom: 12 };

let loading = null;

export function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      if (!document.querySelector(`link[href="${LEAFLET_CSS.href}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = LEAFLET_CSS.href;
        link.integrity = LEAFLET_CSS.integrity;
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      }
      const script = document.createElement('script');
      script.src = LEAFLET_JS.src;
      script.integrity = LEAFLET_JS.integrity;
      script.crossOrigin = 'anonymous';
      script.onload = () => resolve(window.L);
      script.onerror = () => {
        loading = null;
        script.remove();
        reject(new Error('The map couldn\'t load. Check the internet connection and try again.'));
      };
      document.head.appendChild(script);
    });
  }
  return loading;
}

export const tileLayer = (L) => L.tileLayer(TILES.url, { attribution: TILES.attribution, maxZoom: 19 });

// A Leaflet map in the element `ref` points at, made once the library has
// loaded. Returns { map, error }; map is null until it's ready.
export function useLeafletMap(ref, options) {
  const [map, setMap] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let made = null;
    let cancelled = false;
    loadLeaflet().then((L) => {
      if (cancelled || !ref.current) return;
      made = L.map(ref.current, { center: HOME_VIEW.center, zoom: HOME_VIEW.zoom, ...options });
      tileLayer(L).addTo(made);
      setMap(made);
    }).catch((e) => { if (!cancelled) setError(e.message); });
    return () => {
      cancelled = true;
      if (made) made.remove();
      setMap(null);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaflet has to be told when its box changes size (sidebar collapsed, window resized)
  useEffect(() => {
    if (!map || !ref.current) return undefined;
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [map, ref]);

  return { map, error };
}

// Zooms the map to show these [lat, lng] points.
export function fitPoints(map, points, maxZoom = 17) {
  if (!map || !points?.length) return;
  map.fitBounds(window.L.latLngBounds(points), { padding: [30, 30], maxZoom });
}
